import React, { useState } from 'react';
import { Button, Input, Label } from '@lognai/synapse-design-system-v2';
import { useChangePassword } from '../hooks/useAccountSettings';
import { usePasswordPolicy } from '../hooks/usePasswordPolicy';
import { PasswordRequirements } from '@lognai/plugin-components';
import { meetsPasswordPolicy } from '@lognai/plugin-sdk';

/**
 * The one Change Password form.
 *
 * There were two, in parallel: Settings' inline Security card and the account
 * menu's modal, calling the same mutation through two different hooks and
 * duplicating every piece of UI and validation between them — with rules that
 * had already diverged (min 8 in one, min 6 in the other), because a fix to one
 * was never carried to the other (QA ledger AUTH-009). Both now render this.
 *
 * `layout` is the only difference the two surfaces actually needed: the modal
 * puts its buttons in a dialog footer, the card puts one inline.
 */
export interface ChangePasswordFormProps {
  /** Rendered after the fields — the dialog passes its footer here. */
  footer?: (state: { canSubmit: boolean; isPending: boolean }) => React.ReactNode;
  /** Called once the change has succeeded. */
  onSuccess?: () => void;
  idPrefix?: string;
}

export function ChangePasswordForm({
  footer,
  onSuccess,
  idPrefix = 'change-password',
}: ChangePasswordFormProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const policy = usePasswordPolicy();
  const changePassword = useChangePassword();

  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const canSubmit =
    currentPassword.length > 0 &&
    meetsPasswordPolicy(policy, newPassword) &&
    newPassword === confirm;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirm('');
          onSuccess?.();
        },
      }
    );
  };

  return (
    // method="post" is belt-and-braces: handleSubmit calls preventDefault, but a
    // non-JS client must not fall back to GET with the password in the URL.
    <form method="post" className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-current`}>Current password</Label>
        <Input
          id={`${idPrefix}-current`}
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          disabled={changePassword.isPending}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-new`}>New password</Label>
        <Input
          id={`${idPrefix}-new`}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          disabled={changePassword.isPending}
        />
        <PasswordRequirements policy={policy} password={newPassword} className="pt-1" />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-confirm`}>Confirm new password</Label>
        <Input
          id={`${idPrefix}-confirm`}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          disabled={changePassword.isPending}
        />
        {mismatch && <p className="text-sm text-destructive">Passwords do not match.</p>}
      </div>

      {changePassword.isSuccess && (
        <p className="text-sm text-success-foreground">Password updated.</p>
      )}
      {changePassword.isError && (
        <p role="alert" className="text-sm text-destructive">
          {(changePassword.error as Error)?.message || 'Failed to change password'}
        </p>
      )}

      {footer ? (
        footer({ canSubmit, isPending: changePassword.isPending })
      ) : (
        <div>
          <Button type="submit" disabled={!canSubmit || changePassword.isPending}>
            {changePassword.isPending ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      )}
    </form>
  );
}
